import { TIPO_INSPECAO } from "@/contexts/cadastro/estabelecimento";
import { useTermoNotificacao } from "@/contexts/fiscalizacao/termoNotificacao/hooks/useTermoNotificacao";
import { PageLoader } from "@/shared/components/PageLoader/PageLoader";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Grid,
  Input,
  List,
  Radio,
  Result,
  Row,
  Space,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router";

export function VisualizacaoSaneamento() {
  const screens = Grid.useBreakpoint();
  const { id } = useParams<{ id: string }>();
  const { getTermoNotificacaoSaneamentoApi, getAnexoApi } =
    useTermoNotificacao();
  const { message } = App.useApp();
  const navigate = useNavigate();

  const {
    data: termoNotificacao,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["TERMO_NOTIFICACAO", id],
    enabled: !!id,
    queryFn: () => getTermoNotificacaoSaneamentoApi(Number(id)),
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleOpenAnexo = async (nomeUpload?: string) => {
    if (!nomeUpload) return;

    try {
      const anexoBlob = await getAnexoApi(nomeUpload);
      saveAs(anexoBlob, nomeUpload || "anexo");
    } catch (error) {
      message.error("Não foi possível abrir o anexo.");
    }
  };

  useEffect(() => {
    document.title =
      "Inspeção Mobile - Visualização Plano de Ação de Termo de Notificação";
  }, []);

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return (
      <Result
        status="500"
        title="Parece que aconteceu algum problema com o servidor."
        subTitle="Tente novamente mais tarde ou entre em contato com o suporte."
        extra={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
              Voltar
            </Button>
            <Button onClick={() => refetch()}>Tentar Novamente</Button>
          </Space>
        }
      />
    );
  }

  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }} align="center">
        <Typography.Title
          level={screens.xs ? 4 : 3}
          style={{ textAlign: "center" }}
        >
          Plano de Ação do Termo de Notificação {termoNotificacao?.numero}
        </Typography.Title>
      </Space>
      <Divider />

      <Form layout="vertical">
        <Row gutter={[8, 8]}>
          <Col xs={24} sm={12}>
            <Form.Item label="Estabelecimento">
              <Input
                disabled
                value={`${termoNotificacao?.estabelecimento.numeroInspecao} - ${termoNotificacao?.estabelecimento ? TIPO_INSPECAO[termoNotificacao.estabelecimento.tipoInspecao] : "SISE"} - ${termoNotificacao?.estabelecimento.nome}`}
              />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item label="Teor da Notificação">
              <Input.TextArea
                rows={4}
                disabled
                value={termoNotificacao?.teor}
              />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item label="Motivo" rules={[{ required: true }]}>
              <Input
                disabled
                value={termoNotificacao?.motivos
                  .map((motivo) => motivo.nome?.toUpperCase())
                  .join(", ")}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item colon={false} label="Exige Plano de Ação?">
              <Radio.Group disabled value={termoNotificacao?.exigeSaneamento}>
                <Radio value={true}>Sim</Radio>
                <Radio value={false}>Não</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item label="Prazo para atendimento">
              <DatePicker
                format={"DD/MM/YYYY"}
                disabled
                value={
                  termoNotificacao?.prazoAtendimento
                    ? dayjs(termoNotificacao.prazoAtendimento)
                    : undefined
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">
          <Typography.Title level={5}>Plano de Ação</Typography.Title>
        </Divider>

        {termoNotificacao?.saneamentos?.length === 0 && (
          <Typography.Text type="secondary">
            Nenhum plano de ação registrado para este termo de notificação até
            agora.
          </Typography.Text>
        )}

        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          {termoNotificacao?.saneamentos?.map((item) => (
            <Col xs={24} key={item.id}>
              <Card
                title={`Plano de Ação - ${dayjs(item.dataSaneamento).format("DD/MM/YYYY")}`}
                style={{ marginBottom: 16, backgroundColor: "#f6f8fa" }}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <Typography.Text strong>Descrição:</Typography.Text>
                    <Typography.Paragraph>
                      {item.descricao}
                    </Typography.Paragraph>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Typography.Text strong>Registrado por:</Typography.Text>
                    <Typography.Paragraph>
                      {item.usuarioSaneamento?.nome}
                    </Typography.Paragraph>
                  </Col>

                  {item.anexos.length > 0 && (
                    <Col xs={24}>
                      <Typography.Text strong>Anexos:</Typography.Text>
                      <List
                        dataSource={item.anexos}
                        renderItem={(anexo) => (
                          <List.Item>
                            <Button
                              type="link"
                              onClick={() => handleOpenAnexo(anexo.nomeUpload)}
                            >
                              {anexo.nomeArquivo}
                            </Button>
                          </List.Item>
                        )}
                      />
                    </Col>
                  )}

                  {item.dataParecer && (
                    <>
                      <Divider orientation="left" orientationMargin={0}>
                        <Typography.Title level={5}>
                          Parecer -{" "}
                          {dayjs(item.dataParecer).format("DD/MM/YYYY")}
                        </Typography.Title>
                      </Divider>

                      <Col xs={24} sm={12}>
                        <Typography.Text strong>
                          Justificativa Parecer:
                        </Typography.Text>
                        <Typography.Paragraph>
                          {item.justificativaParecer}
                        </Typography.Paragraph>
                      </Col>

                      <Col xs={24} sm={12}>
                        <Typography.Text strong>
                          Registrado por:
                        </Typography.Text>
                        <Typography.Paragraph>
                          {item.usuarioParecer?.nome}
                        </Typography.Paragraph>
                      </Col>

                      <Col xs={24} sm={12}>
                        <Typography.Text strong>Situação:</Typography.Text>
                        <Typography.Paragraph>
                          {item.situacao}
                        </Typography.Paragraph>
                      </Col>

                      {item.prazoFinal && (
                        <Col xs={24} sm={12}>
                          <Typography.Text strong>Prazo Final:</Typography.Text>
                          <Typography.Paragraph>
                            {dayjs(item.prazoFinal).format("DD/MM/YYYY")}
                          </Typography.Paragraph>
                        </Col>
                      )}
                    </>
                  )}
                </Row>
              </Card>
            </Col>
          ))}
        </Row>

        <Divider />

        <Row justify={"center"} gutter={[10, 10]}>
          <Col>
            <Button
              htmlType="button"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              style={{ width: 200 }}
            >
              Voltar
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
}
